/* search.ts — ⌘K fuzzy search over search.json, powered by Fuse.js.
 *
 * Extracted from a ~140-line inline block in _layouts/default.html, which ESLint
 * could not see. Two things are fixed in the move:
 *   - Arrow-key and Enter navigation now exist. The modal footer has always
 *     advertised "↑↓ navigate" and "↵ open", but nothing implemented them.
 *   - navigator.platform is deprecated and empty in some browsers, so non-Mac
 *     users could still be shown "⌘K". Uses userAgent instead.
 *
 * The index is fetched on first open rather than on page load, so a reader who
 * never searches never pays for it.
 */

(function () {
    'use strict';

    /** One entry in search.json, emitted by the Liquid loop in that file. */
    interface Entry {
        title: string;
        url: string;
        content: string;
    }

    const modalEl = document.getElementById('search-modal');
    const inputEl = document.querySelector<HTMLInputElement>('#search-input');
    const triggerEl = document.getElementById('search-trigger');
    const resultsEl = document.getElementById('results-container');
    if (!modalEl || !inputEl || !triggerEl || !resultsEl) {
        return;
    }

    /* Re-bound after the guard. TypeScript's narrowing does not reach into a
       hoisted function declaration, and every handler below is one. */
    const modal = modalEl;
    const input = inputEl;
    const trigger = triggerEl;
    const results = resultsEl;

    const baseurl = document.body.dataset.baseurl || '';
    let isOpen = false;
    let fuse: FuseJs.Instance<Entry> | null = null;
    let loading: Promise<void> | null = null;
    let active = -1;

    if (!/Mac|iP(hone|ad|od)/.test(navigator.userAgent)) {
        const kbd = trigger.querySelector('.search-kbd');
        if (kbd) {
            kbd.textContent = 'Ctrl K';
        }
    }

    function loadIndex() {
        if (loading) {
            return loading;
        }
        loading = fetch(baseurl + '/search.json')
            .then(function (r) { return r.json() as Promise<Entry[]>; })
            .then(function (data) {
                fuse = new Fuse(data, {
                    keys: [
                        { name: 'title', weight: 2 },
                        { name: 'content', weight: 1 },
                    ],
                    threshold: 0.35,
                    ignoreLocation: true,
                    minMatchCharLength: 3,
                    includeScore: true,
                    includeMatches: true,
                    findAllMatches: true,
                });
            });
        return loading;
    }

    function openModal() {
        modal.classList.add('active');
        isOpen = true;
        document.body.style.overflow = 'hidden';
        input.focus();
        loadIndex();
    }

    function closeModal() {
        modal.classList.remove('active');
        input.value = '';
        results.innerHTML = '';
        isOpen = false;
        active = -1;
        document.body.style.overflow = '';
    }

    /* ── Result rendering ─────────────────────────────────────────────── */

    function snippetFor(match: FuseJs.Match | undefined) {
        if (!match || !match.indices || !match.indices.length || !match.value) {
            return '';
        }
        /* Centre the snippet on the longest contiguous match. */
        let best = match.indices[0];
        match.indices.forEach(function (pair) {
            if (pair[1] - pair[0] > best[1] - best[0]) {
                best = pair;
            }
        });
        const text = match.value;
        const start = Math.max(0, best[0] - 50);
        const end = Math.min(text.length, best[1] + 70);
        let out = text.substring(start, end).replace(/\s+/g, ' ').trim();
        if (start > 0) {
            out = '…' + out;
        }
        if (end < text.length) {
            out += '…';
        }
        return out;
    }

    /* search.json holds rendered text, so entities can survive into it. Decode
       via textContent rather than innerHTML: assigning untrusted text to
       innerHTML to "decode" it is how you get script injection. */
    function decode(text: string) {
        const el = document.createElement('textarea');
        el.innerHTML = text;
        return el.value;
    }

    function highlight(index: number) {
        const items = Array.from(results.querySelectorAll('a'));
        items.forEach(function (a, i) {
            a.classList.toggle('is-active', i === index);
        });
        if (index >= 0 && items[index]) {
            items[index].scrollIntoView({ block: 'nearest' });
        }
    }

    function render(query: string) {
        results.innerHTML = '';
        active = -1;
        if (!fuse || query.length < 2) {
            return;
        }

        const hits = fuse.search(query).slice(0, 8);
        if (hits.length === 0) {
            const li = document.createElement('li');
            const span = document.createElement('span');
            span.className = 'no-results';
            span.textContent = 'No results for "' + query + '"';
            li.style.padding = '14px 20px';
            li.appendChild(span);
            results.appendChild(li);
            return;
        }

        hits.forEach(function (hit) {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.href = hit.item.url;

            const title = document.createElement('div');
            title.className = 'search-result-title';
            title.textContent = hit.item.title;
            a.appendChild(title);

            let snippet = '';
            if (hit.matches) {
                snippet = snippetFor(hit.matches.find(function (m) { return m.key === 'content'; }));
            }
            if (!snippet && hit.item.content) {
                snippet = hit.item.content.substring(0, 140).replace(/\s+/g, ' ').trim() + '…';
            }

            if (snippet) {
                const text = decode(snippet);
                /* Scroll-to-Text Fragment so the result lands on the match. */
                const target = text.replace(/…/g, '').trim().substring(0, 50);
                if (target.length > 5) {
                    a.href = hit.item.url + '#:~:text=' + encodeURIComponent(target);
                }
                const el = document.createElement('div');
                el.className = 'search-result-snippet';
                el.textContent = text;
                a.appendChild(el);
            }

            li.appendChild(a);
            results.appendChild(li);
        });
    }

    /* ── Events ───────────────────────────────────────────────────────── */

    trigger.addEventListener('click', openModal);
    modal.addEventListener('click', function (e) {
        if (e.target === modal) {
            closeModal();
        }
    });

    input.addEventListener('input', function () {
        const query = this.value.trim();
        if (fuse) {
            render(query);
        } else {
            loadIndex().then(function () { render(query); });
        }
    });

    document.addEventListener('keydown', function (e) {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
            e.preventDefault();
            if (isOpen) {
                closeModal();
            } else {
                openModal();
            }
            return;
        }
        if (!isOpen) {
            return;
        }
        if (e.key === 'Escape') {
            closeModal();
            return;
        }

        const items = Array.from(results.querySelectorAll('a'));
        if (items.length === 0) {
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            active = (active + 1) % items.length;
            highlight(active);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            active = (active - 1 + items.length) % items.length;
            highlight(active);
        } else if (e.key === 'Enter' && active >= 0) {
            e.preventDefault();
            items[active].click();
        }
    });
})();
